import { useState, useEffect, useCallback } from "react";
import { ReactNode } from "react";
import "./ProjectDetail.scss";
import OverviewSection from "./sections/OverviewSection";
import AIASection from "./sections/AIASection";
import DrawHistorySection from "./sections/DrawHistory/DrawHistorySection";
import {
  buildAiaTabSubItems,
  type AiaTabId,
} from "./sections/DrawHistory/DrawHistory.types";
import ProgressSection from "./sections/ProgressSection";
import PreconSection from "./sections/PreconSection";
import ContactsSection from "./sections/ContactsSection";
import DocumentsSection from "./sections/DocumentsSection";
import Sidebar from "../../../Components/Sidebar/Sidebar";
import OverviewIcon from "Assets/Img/OverviewIcon";
import DrawRequestIcon from "Assets/Img/DrawRequestIcon";
import HistoryIcon from "Assets/Img/HistoryIcon";
import ProgressIcon from "Assets/Img/ProgressIcon";
import Contacts from "Assets/Img/Contacts";
import DocIcon from "Assets/Img/DocIcon";
import MessageIcon from "Assets/Img/MessageIcon";
import ConversationPanel from "../../../Components/Common/ConversationPanel/ConversationPanel";
import { useParams, useNavigate } from "react-router-dom";
import Establish from "Assets/Img/Establish";
import ProjectType from "Assets/Img/ProjectType";
import ProjectTitleIcon from "Assets/Img/ProjectTitleIcon";
import ChangeOrderIcon from "Assets/Img/ChangeOrderIcon";
import PreconChecklistIcon from "Assets/Img/PreconChecklistIcon";
import RequirementsAcknowledgedIcon from "Assets/Img/RequirementsAcknowledgedIcon";
import ScheduleOfValuesIcon from "Assets/Img/ScheduleOfValuesIcon";
import PreConstructionReportIcon from "Assets/Img/PreConstructionReportIcon";
import AdminChangeOrders from "./sections/AdminChangeOrders";
import PreconChecklistSection from "./sections/PreconChecklistSection";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import {
  getProject,
  getProjectOverview,
  getProjectDocuments,
  getAdminPreconReview,
  getAdminPreconChecklist,
} from "../../../services/projectService";
import { getSovVersions } from "services/sovSetupService";
import SOVSetup from "Pages/GcPrecon/GcProjectDetails/components/sections/SOVSetup";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import NoDocuments from "Assets/Img/NoDocuments";
import { formatDate } from "utils/Common/Date";
import useScrollToTop from "hooks/useScrollToTop";
import RequirementsAcknowledgedSection from "./sections/RequirementsAcknowledgedSection";

interface SovVersion {
  id: string;
  versionNumber: number;
  status: string;
}

const Icons = {
  overview: <OverviewIcon />,
  "change-orders": <ChangeOrderIcon />,
  aia: <DrawRequestIcon />,
  progress: <ProgressIcon />,
  precon: <PreConstructionReportIcon />,
  "requirements-acknowledged": <RequirementsAcknowledgedIcon />,
  "precon-checklist": <PreconChecklistIcon />,
  contacts: <Contacts />,
  documents: <DocIcon />,
  messages: <MessageIcon />,
};
interface NavItem {
  id: string;
  label: string;
}

interface NavGroup {
  group: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    group: "Project",
    items: [
      { id: "overview", label: "Overview" },
      // { id: "lien-items-progress", label: "Lien Items Progress" },
      {
        id: "change-orders",
        label: "Change Orders",
      },
    ],
  },
  {
    group: "Financials",
    items: [{ id: "aia", label: "AIA Summary" }],
  },
  {
    group: "Reports",
    items: [
      { id: "progress", label: "Progress Reports" },
      { id: "precon", label: "Pre-Construction" },
    ],
  },
  {
    group: "Project Info",
    items: [
      { id: "contacts", label: "Contacts & Lender" },
      { id: "documents", label: "Documents & Files" },
      { id: "precon-checklist", label: "Pre-Con Checklist" },
      {
        id: "requirements-acknowledged",
        label: "Requirements Acknowledged",
      },
    ],
  },
  {
    group: "Communication",
    items: [{ id: "messages", label: "Messages" }],
  },
];

const SECTIONS: Record<string, ReactNode> = {};

const fmtStatus = (s: string) =>
  s
    .split("_")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

const statusClass = (s: string) => {
  if (s === "active" || s === "in_progress") return "b-active";
  if (s === "completed") return "b-green";
  return "b-review";
};

export default function ProjectDetail() {
  const { id: projectId, activeTab } = useParams();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(activeTab ?? "overview");

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    navigate(`/project-detail/${projectId}/${section}`, { replace: true });
  };
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [projectOverview, setProjectOverview] = useState<any>(null);
  const [documents, setDocuments] = useState<Record<string, any[]> | null>(
    null,
  );
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [preconData, setPreconData] = useState<any>(null);
  const [preconLoading, setPreconLoading] = useState(false);
  const [checklistData, setChecklistData] = useState<any>(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [sovVersions, setSovVersions] = useState<SovVersion[]>([]);
  const [sovVersionsLoaded, setSovVersionsLoaded] = useState(false);
  const [drawBadges, setDrawBadges] = useState({
    invoiceCount: 0,
    waiverProgress: "",
  });

  // Stable reference: DrawHistorySection's effect depends on this, so it
  // must not change identity every render or the two would loop each other.
  const handleDrawBadgesChange = useCallback(
    (invoiceCount: number, waiverProgress: string) => {
      setDrawBadges({ invoiceCount, waiverProgress });
    },
    [],
  );

  useScrollToTop(activeSection);

  // "Draw History" has no content of its own — like "Schedule of Values",
  // selecting the bare parent immediately drops into its first child tab.
  useEffect(() => {
    if (activeSection !== "draws") return;
    setActiveSection("draws-summary");
    navigate(`/project-detail/${projectId}/draws-summary`, { replace: true });
  }, [activeSection, projectId]);

  useEffect(() => {
    if (!projectId || activeSection !== "documents") return;
    setDocumentsLoading(true);
    (getProjectDocuments(projectId) as Promise<{ data: any; error: any }>).then(
      ({ data, error }) => {
        if (!error && data) setDocuments(data?.data ?? {});
        setDocumentsLoading(false);
      },
    );
  }, [projectId, activeSection]);

  useEffect(() => {
    if (!projectId || activeSection !== "precon") return;
    setPreconLoading(true);
    (
      getAdminPreconReview(projectId) as Promise<{ data: any; error: any }>
    ).then(({ data, error }) => {
      if (!error && data) setPreconData(data?.data ?? null);
      setPreconLoading(false);
    });
  }, [projectId, activeSection]);

  useEffect(() => {
    if (!projectId || activeSection !== "precon-checklist") return;
    setChecklistLoading(true);
    (
      getAdminPreconChecklist(projectId) as Promise<{ data: any; error: any }>
    ).then(({ data, error }) => {
      if (!error && data) setChecklistData(data?.data ?? null);
      setChecklistLoading(false);
    });
  }, [projectId, activeSection]);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    (getProject(projectId) as Promise<{ data: any; error: any }>).then(
      ({ data, error }) => {
        if (!error && data) setProject(data?.data ?? data);
        setLoading(false);
      },
    );
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    (getProjectOverview(projectId) as Promise<{ data: any; error: any }>).then(
      ({ data, error }) => {
        if (!error && data) setProjectOverview(data?.data ?? data);
        setLoading(false);
      },
    );
  }, [projectId]);
  useEffect(() => {
    if (!projectId) return;
    getSovVersions(projectId).then(
      ({ data }: { data: { data?: SovVersion[] } | null }) => {
        if (data && Array.isArray(data.data)) {
          setSovVersions(
            [...data.data].sort((a, b) => b.versionNumber - a.versionNumber),
          );
        }
        setSovVersionsLoaded(true);
      },
    );
  }, [projectId]);

  useEffect(() => {
    if (activeSection !== "sov-version" || sovVersions.length === 0) return;
    const firstId = sovVersions[0].id;
    setActiveSection(`sov-version-${firstId}`);
    navigate(`/project-detail/${projectId}/sov-version-${firstId}`, {
      replace: true,
    });
  }, [activeSection, sovVersions]);

  const status = project?.status ?? "";
  const progress = project?.progressPercentage
    ? parseFloat(project.progressPercentage)
    : 0;

  return (
    <div className="projectDetailSec">
      <div className="pd-banner">
        <div className="pb-top">
          <div className="pb-icon">
            <ProjectTitleIcon />
          </div>

          <div className="pb-info">
            <div className="pb-name">
              {loading ? (
                <Skeleton width={200} height={18} />
              ) : (
                (project?.name ?? "—")
              )}
            </div>
            <div className="pb-meta">
              {loading ? (
                <Skeleton width={340} height={12} />
              ) : (
                <>
                  {project?.type && (
                    <div className="pb-meta-item">
                      <ProjectType />
                      {fmtStatus(project.type)}
                      {project.constructionType
                        ? ` · ${fmtStatus(project.constructionType)}`
                        : ""}
                    </div>
                  )}
                  {project?.estCompletionDate && (
                    <div className="pb-meta-item">
                      <Establish />
                      Est. completion:{" "}
                      {formatDate(project.estCompletionDate, false)}
                    </div>
                  )}
                  {project?.progressReport && (
                    <div className="pb-meta-item">
                      <DrawRequestIcon />
                      Report #
                      {typeof project.progressReport === "object"
                        ? project.progressReport.reportNumber
                        : ""}
                      <div>
                        {project?.progressReport && (
                          <div className="pb-meta-item">
                            {typeof project.progressReport === "object"
                              ? project.progressReport.reportDate
                                ? `· ${formatDate(project.progressReport.reportDate, false)}`
                                : ""
                              : ""}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {(project?.bankName || project?.branchName) && (
                    <div className="pb-meta-item">
                      {[project.bankName, project.branchName]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="pb-right">
            {loading ? (
              <>
                <Skeleton width={80} height={24} borderRadius={12} />
                <Skeleton width={120} height={16} style={{ marginTop: 8 }} />
              </>
            ) : (
              <>
                {status && (
                  <span className={`badge ${statusClass(status)}`}>
                    <span className="bdot" />
                    {fmtStatus(status)}
                  </span>
                )}
                <div className="prog-wrap">
                  <span className="prog-label">{progress.toFixed(0)}%</span>
                  <div className="prog-track">
                    <div
                      className="prog-fill"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="pd-body">
        <Sidebar
          sections={[
            ...NAV_GROUPS.map(({ group, items }) => ({
              label: group,
              items: items.flatMap((item) => {
                const withIcon = {
                  ...item,
                  icon: Icons[item.id as keyof typeof Icons],
                };
                // "Draw History" belongs right after "AIA Summary" in Financials
                if (item.id === "aia") {
                  return [
                    withIcon,
                    {
                      id: "draws",
                      label: "Draw History",
                      icon: <HistoryIcon />,
                      subItems: buildAiaTabSubItems(
                        "draws",
                        drawBadges.invoiceCount,
                        drawBadges.waiverProgress,
                      ),
                    },
                  ];
                }
                return [withIcon];
              }),
            })),
            {
              label: "Review SOV",
              items: [
                {
                  id: "sov-version",
                  label: "Schedule of Values",
                  icon: <ScheduleOfValuesIcon />,
                  subItems: sovVersions.map((v) => ({
                    id: `sov-version-${v.id}`,
                    versionNumber: v.versionNumber,
                    label: `Version ${v.versionNumber}`,
                    badge: {
                      text: v.status === "superseded" ? "Inactive" : v.status,
                      variant: v.status === "active" ? "view" : "",
                    },
                  })),
                },
              ],
            },
          ]}
          activeSection={activeSection}
          onSelect={handleSectionChange}
        />

        <main className="pd-main">
          {activeSection === "overview" ? (
            <OverviewSection
              projectOverview={projectOverview}
              loading={loading}
              projectId={projectId}
            />
          ) : activeSection === "contacts" ? (
            <ContactsSection
              stakeholders={projectOverview?.stakeholders}
              loading={loading}
              preConstructionStatus={projectOverview?.preConstructionStatus}
            />
          ) : activeSection === "aia" ? (
            <AIASection projectId={projectId ?? ""} />
          ) : activeSection === "change-orders" ? (
            <AdminChangeOrders projectId={projectId ?? ""} />
          ) : // ) : activeSection === "lien-items-progress" ? (
          //   <LienItemsProgressSection />
          activeSection === "progress" ? (
            <ProgressSection projectId={projectId ?? ""} />
          ) : activeSection === "documents" ? (
            <DocumentsSection
              documents={documents}
              loading={documentsLoading}
            />
          ) : activeSection === "precon" ? (
            <PreconSection
              preconData={preconData}
              loading={preconLoading}
              projectId={projectId}
              preconReportId={preconData?.id}
            />
          ) : activeSection === "precon-checklist" ? (
            <PreconChecklistSection
              data={checklistData}
              loading={checklistLoading}
            />
          ) : activeSection === "requirements-acknowledged" ? (
            <RequirementsAcknowledgedSection projectId={projectId ?? ""} />
          ) : (
            SECTIONS[activeSection]
          )}
          {activeSection === "sov-version" &&
            sovVersionsLoaded &&
            sovVersions.length === 0 && (
              <div className="ds active">
                <EmptyState
                  icon={<NoDocuments />}
                  title="Schedule of Values Not Submitted"
                  subtitle="The GC has not yet submitted a Schedule of Values for this project."
                  noBorder
                />
              </div>
            )}
          <SOVSetup
            isActive={activeSection.startsWith("sov-version-")}
            projectId={projectId}
            versionId={
              activeSection.startsWith("sov-version-")
                ? activeSection.replace("sov-version-", "")
                : undefined
            }
          />
          <DrawHistorySection
            isActive={activeSection.startsWith("draws-")}
            activeTab={
              (activeSection.startsWith("draws-")
                ? activeSection.replace("draws-", "")
                : "summary") as AiaTabId
            }
            projectId={projectId ?? ""}
            projectName={project?.name}
            onBadgesChange={handleDrawBadgesChange}
          />
          <ConversationPanel
            projectId={projectId ?? ""}
            isActive={activeSection === "messages"}
          />
        </main>
      </div>
    </div>
  );
}
